import {
  BackEndValueType,
  ECardinality,
  ESdChangeReason,
  EWFParamType,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  IKuikaDataType,
  ISymbol,
  IWFParameter,
  IWFStep,
  IWorkFlow
} from "@kuika/kuika-cl-model";
import { IStepParameter } from "@kuika/kuika-cl-model/src/models/workflow";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import { getDefaultDesignerActionSymbol } from "../../../../../shared/utility/designer-action-actions";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { getWorkflowParamTypeLabel } from "../../../left-panel/work-flow-list/workflow-parameter-type-options";
import { EActionPanelStatus } from "../../properties-panel/action-panel";
import { ActionPicker } from "../../properties-panel/action-picker";
import ActionSectionEmptyState from "../../properties-panel/action-section-empty-state";
import { ActionSymbolWithChild } from "../../properties-panel/action-symbol-with-child";
import { SymbolPicker } from "../../properties-panel/symbol-picker";
import { getBpmnLoopState } from "../wf-utils/loop-utils";
import { getBpmnMiState, watchBpmnMi } from "../wf-utils/multi-instance-utils";
import { WorkflowStepOutputMapping } from "../wf-utils/workflow-step-output-mapping";
import "./workflow-system-step-properties.scss";
import { AggregationProperties } from "./aggregation-properties";
import { LoopDecisionProperties } from "./loop-decision-properties";
import { PmiDecisionProperties } from "./pmi-decision-properties";
import { SmiDecisionProperties } from "./smi-decision-properties";

export interface IWorkflowSystemStepPropertiesState {
  resumePanelActive: boolean;
  miStamp?: number;
}

export interface IWorkflowSystemStepPropertiesProps {}

/** The { action, parameter } pair an ActionSymbolWithChild needs for an object output param. */
export interface IOutputObjectContext {
  action: IAction;
  parameter: IActionParameter;
}

class WorkflowSystemStepProperties extends PureSdComponent<
  IWorkflowSystemStepPropertiesProps & IWithSDProps & IWithContextProp,
  IWorkflowSystemStepPropertiesState
> {
  private _miUnsub?: () => void;

  constructor(props: IWorkflowSystemStepPropertiesProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      resumePanelActive: true
    };
  }

  onUpdateSelectedParamsToMap = (selectedParams) => {
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    this.props.sd.setWFStepParameters(selectedStep!, selectedParams, EWFParamType.Output);
  };

  updateOutputParameterMapping = (parameter: IStepParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    if (wf && selectedStep && selectedStep.outputParameters && selectedStep.outputParameters.length > 0) {
      let param = selectedStep.outputParameters.find((p) => p.id == parameter.id);
      if (param) param.value = symbol;
      this.props.sd.saveWorkFlow(wf, changeReason);
    }
  };

  // Expands a Kuika Data Type into nested IActionParameter[] (with ownerPropName so the picker's
  // getParentParameter resolves the object as the parent → element-field context for list mapping).
  // Seeds each field's symbol from the persisted StepParameterModel.parameters mapping.
  catalogTypeToActionParameters = (
    dataTypeName: string | undefined,
    existing: IStepParameter[] | undefined,
    ownerPropName: string,
    depth: number = 0
  ): IActionParameter[] => {
    if (!dataTypeName || depth > 5) return [];
    const catalogType = (this.props.sd.types ?? []).find((t) => t.name === dataTypeName) as unknown as
      | IKuikaDataType
      | undefined;
    return (catalogType?.elements ?? []).map((f): IActionParameter => {
      const referenced = (this.props.sd.types ?? []).find((t) => t.name === f.type) as unknown as
        | IKuikaDataType
        | undefined;
      // Only a type that actually has nested fields is an object. A scalar (e.g. "String") may still
      // resolve to a bare type entry in sd.types, so guard on elements to avoid a false drawer carrot.
      const isObjectField: boolean = !f.isList && (referenced?.elements?.length ?? 0) > 0;
      const existingField: IStepParameter | undefined = existing?.find((e) => e.name === f.name);
      const param: IActionParameter = {
        name: f.name,
        realPropName: f.name,
        ownerPropName,
        Typename: f.type as BackEndValueType,
        cardinality: f.isList ? ECardinality.Many : ECardinality.One,
        symbol: existingField?.value ?? getDefaultDesignerActionSymbol(f.type),
        isObject: isObjectField || undefined
      };
      // Seed nested fields from the persisted hierarchical tree (existingField.parameters) so reloaded
      // deep mappings display; children carry this field's name as their owner for path resolution.
      if (isObjectField)
        param.parameters = this.catalogTypeToActionParameters(f.type, existingField?.parameters, f.name, depth + 1);
      return param;
    });
  };

  // Builds the { action, parameter } context an ActionSymbolWithChild needs for an object output param.
  // The synthetic action carries the top object param in `parameters` so getParentParameter can find it
  // (element-field context for lists); its shape is spread from a step action for a valid IAction.
  buildOutputObjectContext = (
    p: IStepParameter,
    dataTypeName: string,
    cardinality: ECardinality
  ): IOutputObjectContext => {
    const topParam: IActionParameter = {
      name: p.name ?? "",
      realPropName: p.name ?? "",
      Typename: dataTypeName as BackEndValueType,
      cardinality,
      isObject: true,
      symbol: p.value ?? getDefaultDesignerActionSymbol(dataTypeName),
      parameters: this.catalogTypeToActionParameters(dataTypeName, p.parameters, p.name ?? "")
    };
    const stepAction: IAction | undefined = this.props.sd.getSelectedWfStep()?.actions?.[0];
    const action: IAction = { ...(stepAction ?? ({} as IAction)), id: `${p.id}_outObj`, parameters: [topParam] };
    return { action, parameter: topParam };
  };

  getStepActionDefinition = (): IActionDefinitionDto | undefined => {
    const stepAction = this.props.sd.getSelectedWfStep()?.actions?.[0];
    return this.props.sd.actionDefinitions?.find((ad) => ad.id === stepAction?.actionDefinitionId);
  };

  // Persistence adapter for the object output drawer: the top selection sets the output param's value;
  // a nested field selection is written into the StepParameterModel.parameters tree at its true path so
  // the backend (AppendFieldInitializers, which recurses field.Parameters and assembles nested objects
  // at any depth) reconstructs the same shape. Nested fields nest hierarchically — NOT a flat list —
  // so a nested Company.Name and the object's own Name never collide.
  onOutputObjectParameterSelected = (
    stepParamId: string,
    topName: string,
    dataTypeName: string,
    parameter: IActionParameter,
    symbol: ISymbol,
    changeReason?: ESdChangeReason
  ): void => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const outputParam = this.props.sd.getSelectedWfStep()?.outputParameters?.find((op) => op.id === stepParamId);
    if (!wf || !outputParam) return;
    if (parameter.name === topName) {
      outputParam.value = symbol;
    } else {
      if (!outputParam.parameters) outputParam.parameters = [];
      // Rebuild the catalog field tree and resolve this leaf's full path (e.g. ["Company","Name"]) so
      // the symbol lands under the correct nested parent instead of a flat same-named entry.
      const fieldTree: IActionParameter[] = this.catalogTypeToActionParameters(dataTypeName, undefined, topName);
      const path: string[] = this.resolveCatalogFieldPath(fieldTree, parameter.name, parameter.ownerPropName) ?? [
        parameter.name
      ];
      this.upsertNestedStepParameter(outputParam.parameters, path, symbol, stepParamId);
    }
    this.props.sd.saveWorkFlow(wf, changeReason);
  };

  // Depth-first search of the catalog-built field tree for the edited leaf, returning the chain of field
  // names from the top object down to the leaf. Matches on (name, owner) — owner being the parent field's
  // name that catalogTypeToActionParameters stamps on each child — so a same-named field at a different
  // level is not mistaken for this one.
  resolveCatalogFieldPath = (
    nodes: IActionParameter[],
    targetName: string,
    targetOwner: string | undefined,
    prefix: string[] = []
  ): string[] | null => {
    for (const node of nodes) {
      const here: string[] = [...prefix, node.name];
      if (node.name === targetName && (node.ownerPropName ?? "") === (targetOwner ?? "")) return here;
      if (node.parameters && node.parameters.length > 0) {
        const found: string[] | null = this.resolveCatalogFieldPath(node.parameters, targetName, targetOwner, here);
        if (found) return found;
      }
    }
    return null;
  };

  // Upserts a symbol at a nested path in a StepParameterModel tree, creating intermediate object nodes
  // (name only, no value) as needed. The leaf node carries the source symbol as its value.
  upsertNestedStepParameter = (params: IStepParameter[], path: string[], symbol: ISymbol, idPrefix: string): void => {
    let level: IStepParameter[] = params;
    for (let i = 0; i < path.length; i++) {
      const name: string = path[i];
      let node: IStepParameter | undefined = level.find((x) => x.name === name);
      if (!node) {
        node = { id: `${idPrefix}.${path.slice(0, i + 1).join(".")}`, name };
        level.push(node);
      }
      if (i === path.length - 1) {
        node.value = symbol;
      } else {
        if (!node.parameters) node.parameters = [];
        level = node.parameters;
      }
    }
  };

  resolveSelectedStep = (): IWFStep | undefined => {
    const bySel = this.props.sd.getSelectedWfStep?.();
    if (bySel?.id) return bySel;
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const nid = this.props.sd?.selectedWfNode?.id;
    const fromNodes = nid ? wf?.nodes?.steps?.find((s) => s.id === nid) : undefined;
    return fromNodes;
  };

  /** Leading glyph for a section head, before the title. */
  renderSectionIcon = (name: string): JSX.Element => (
    <KuikaSvg name={name} width={14} height={14} color="var(--dark-9)" className="kuika-wf-step__section-icon" />
  );

  /** The section-title info glyph; wraps in a hover tooltip when hint copy is supplied. */
  renderSectionInfo = (tooltip?: string): JSX.Element => {
    const icon: JSX.Element = <KuikaSvg name="info-circle" width={13} height={13} color="var(--dark-5)" />;
    if (!tooltip) return icon;
    return <KuikaTooltip content={tooltip}>{icon}</KuikaTooltip>;
  };

  // Rendered inline after Step Outputs and gated on the live BPMN multi-instance state, so a
  // plain system step shows no empty surface while a multi-instance one still gets its config.
  renderMultiInstanceSection = (): JSX.Element | null => {
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const step: IWFStep | undefined = this.resolveSelectedStep();
    if (!wf || !step?.id) return null;

    const mi = getBpmnMiState(step.id);
    if (!mi.enabled) return null;

    return (
      <section className="kuika-wf-step__section">
        <div className="kuika-wf-step__section-head">
          {this.renderSectionIcon("layers")}
          <span className="kuika-wf-step__section-title">Multi-Instance</span>
        </div>
        {mi.mode === "PMI" && <PmiDecisionProperties step={step} />}
        {mi.mode === "SMI" && <SmiDecisionProperties step={step} />}
        <AggregationProperties step={step} />
      </section>
    );
  };

  getSelectedStepId = (): string | undefined => {
    const step = this.props.sd.getSelectedWfStep?.();
    return step?.id ?? this.props.sd?.selectedWfNode?.id;
  };

  attachMiWatcher = () => {
    this.detachMiWatcher();
    const stepId = this.getSelectedStepId();
    if (!stepId) return;
    this._miUnsub = watchBpmnMi(stepId, () => {
      this.setState({ miStamp: Date.now() });
    });
  };

  detachMiWatcher = () => {
    try {
      this._miUnsub && this._miUnsub();
    } finally {
      this._miUnsub = undefined;
    }
  };

  // Same inline treatment as multi-instance: gated on the same loop state the child reads, so
  // the section head never outlives the panel it titles.
  renderLoopSection = (): JSX.Element | null => {
    const step: IWFStep | undefined = this.resolveSelectedStep?.() ?? this.props.sd.getSelectedWfStep?.();
    if (!step?.id) return null;
    const isLoopEnabled: boolean = getBpmnLoopState(step.id).enabled;
    if (!isLoopEnabled) return null;
    return (
      <section className="kuika-wf-step__section">
        <div className="kuika-wf-step__section-head">
          {this.renderSectionIcon("repeat")}
          <span className="kuika-wf-step__section-title">Loop</span>
        </div>
        <LoopDecisionProperties step={step} />
      </section>
    );
  };

  componentDidMount(): void {
    this.attachMiWatcher();
  }

  componentDidUpdate(prevProps): void {
    const prevId = prevProps?.sd?.getSelectedWfStep?.()?.id ?? prevProps?.sd?.selectedWfNode?.id;
    const curId = this.props.sd?.getSelectedWfStep?.()?.id ?? this.props.sd?.selectedWfNode?.id;
    if (prevId !== curId) {
      this.attachMiWatcher();
    }
  }

  componentWillUnmount(): void {
    this.detachMiWatcher();
  }

  /**
   * One Step Outputs entry: the parameter's name/type row stacked over the picker that maps it.
   * Object/list params delegate the whole block to ActionSymbolWithChild, which renders its own
   * label, the picker, and the nested field-mapping drawer.
   */
  renderOutputParameter = (p: IStepParameter, wf: IWorkFlow | undefined): JSX.Element => {
    const wfPrm: IWFParameter | undefined = wf?.parameters?.find((wp) => wp.id === p.id);
    if (wfPrm === undefined) return <></>;

    // Object/list output param: reuse ActionSymbolWithChild — it renders the picker,
    // the List<x>/type label, and the inner field-mapping drawer (with element-field
    // context for lists). A persistence adapter writes selections into the step
    // output param (top value + per-field parameters); backend assembles the object.
    if (wfPrm.isObject && wfPrm.dataTypeName) {
      const ctx: IOutputObjectContext = this.buildOutputObjectContext(
        p,
        wfPrm.dataTypeName,
        wfPrm.cardinality ?? ECardinality.One
      );
      return (
        <div key={p.id} className="kuika-wf-step__output">
          <ActionSymbolWithChild
            parameter={ctx.parameter}
            action={ctx.action}
            actionDefinition={this.getStepActionDefinition()!}
            actionPanelStatus={EActionPanelStatus.new}
            onActionParameterSelected={(parameter, symbol, changeReason) =>
              this.onOutputObjectParameterSelected(
                p.id,
                ctx.parameter.name,
                wfPrm.dataTypeName!,
                parameter,
                symbol,
                changeReason ?? ESdChangeReason.WFStepMappingChange
              )
            }
          />
        </div>
      );
    }

    const targetTypeName: string = String(wfPrm.dataType);
    const targetCardinality: ECardinality = wfPrm.cardinality ?? ECardinality.One;
    return (
      <div key={p.id} className="kuika-wf-step__output">
        <div className="kuika-wf-step__param">
          <span className="kuika-wf-step__param-icon">{this.props.sd.getTypeIcon(wfPrm.dataType.toString())}</span>
          <span className="kuika-wf-step__param-name">{wfPrm.name}</span>
          <span className="kuika-wf-step__param-type">({getWorkflowParamTypeLabel(wfPrm)})</span>
        </div>
        <SymbolPicker
          changeType="onBlur"
          symbol={p.value}
          valueType={targetTypeName as BackEndValueType}
          targetCardinality={targetCardinality}
          hideWFParams={false}
          onSelectedSymbol={(symbol: ISymbol) =>
            this.updateOutputParameterMapping(wfPrm, symbol, ESdChangeReason.WFStepMappingChange)
          }
        />
      </div>
    );
  };

  render() {
    const selectedStep: IWFStep | undefined = this.props.sd.getSelectedWfStep();
    const wf: IWorkFlow | undefined = this.props.sd.selectedWf;
    const outputParameters: IStepParameter[] = selectedStep?.outputParameters ?? [];

    return (
      <div className="kuika-wf-step">
        <section className="kuika-wf-step__section">
          <div className="kuika-wf-step__section-head">
            {this.renderSectionIcon("bolt")}
            <span className="kuika-wf-step__section-title">Step Actions</span>
            {this.renderSectionInfo("Actions the process runs when it reaches this step.")}
          </div>
          <ActionPicker hideHeader />
        </section>

        <section className="kuika-wf-step__section">
          <div className="kuika-wf-step__section-head">
            {this.renderSectionIcon("arrows-horizontal")}
            <span className="kuika-wf-step__section-title">Step Outputs</span>
            {this.renderSectionInfo("Map the process parameters this step writes when it completes.")}
            <WorkflowStepOutputMapping
              wf={wf!}
              step={selectedStep!}
              onUpdateSelectedParamsToMap={(selectedParams) => this.onUpdateSelectedParamsToMap(selectedParams)}
              triggerIcon
              triggerClassName="kuika-wf-step__add"
            />
          </div>
          {outputParameters.length > 0 ? (
            <div className="kuika-wf-step__output-list">
              {outputParameters.map((p) => this.renderOutputParameter(p, wf))}
            </div>
          ) : (
            <ActionSectionEmptyState
              message="No outputs mapped yet."
              hint="Pick the parameters this step writes."
              flush
            />
          )}
        </section>

        {this.renderMultiInstanceSection()}
        {this.renderLoopSection()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.SaveWorkflow ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.ClickWfDesignXmlNode ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel
    ) {
      return true;
    }

    return false;
  };
}

const workflowSystemStepProperties = withContext<IWorkflowSystemStepPropertiesProps>(
  withSD<IWorkflowSystemStepPropertiesProps>(WorkflowSystemStepProperties)
);
export { workflowSystemStepProperties as WorkflowSystemStepProperties };
